/**
 * Sync-stream progress aggregation — the ONE source of first-sync progress for
 * every platform (web, Tauri desktop, Tauri mobile).
 *
 * ─── Why this exists ────────────────────────────────────────────────────────
 *
 * `SyncStatus.downloadProgress` (the global, bucket-based progress reported by
 * the PowerSync core extension) is NOT available under Tauri. The Rust bridge
 * in `tauri-plugin-powersync` deliberately serialises an EMPTY bucket map:
 *
 *   // tauri-plugin-powersync 0.0.4 — src/database.rs
 *   inner.serialize_field("downloadProgress",
 *       if status.is_downloading() { &Some(EmptyDownloadProgress {}) } else { &None })?;
 *
 *   // tauri-plugin-powersync 0.0.6 — src/database.rs (same call, explicit note)
 *   // Note: Priority status and download progress is not available outside of streams.
 *
 * The upstream `powersync` crate cannot do better: it drops the bucket map at
 * deserialisation time (`downloading: Option<IgnoredAny>` in
 * `sync::instruction::DownloadSyncStatus`), so the data never reaches the
 * bridge in the first place.
 *
 * The practical consequence is a trap rather than an obvious failure:
 * `status.downloadProgress` is **non-null while downloading**, so a `!= null`
 * check passes — but every counter inside it is 0, so a progress bar built on
 * `downloadedFraction` sits frozen at 0% and NEVER reaches 1.
 *
 * ─── What IS available ──────────────────────────────────────────────────────
 *
 * Per-sync-stream counters. Both bridges forward `internalStreamSubscriptions`
 * verbatim from the core extension, and `@powersync/common` exposes them as
 * {@link SyncStatus.syncStreams} / {@link SyncStatus.forStream}. Each entry
 * carries `progress: { total, downloaded }` and `last_synced_at`, which
 * `@powersync/common` surfaces as `subscription.hasSynced`.
 *
 * The web SDK receives the SAME structure from the SAME core extension, so
 * aggregating over streams works identically on every platform. That is the
 * whole point of this module: ONE code path, no `isTauriRuntime()` branch, so
 * a green check on web is evidence about Tauri instead of a separate story.
 *
 * ─── Not available anywhere under Tauri ─────────────────────────────────────
 *
 *   - `status.hasSynced` / `status.lastSyncedAt` / `status.priorityStatusEntries`
 *     — never serialised by the bridge (`undefined` / `[]`). Use
 *     {@link aggregateStreamProgress}'s `hasSynced` instead.
 *   - per-stream `priority` — the field is commented out in the Rust
 *     `ActiveStreamSubscription`, so progress-until-priority cannot be built.
 *   - `db.waitForFirstSync()` — implemented as `waitForStatus(s => s.hasSynced)`,
 *     which never resolves under Tauri. Use {@link streamsHaveSynced}.
 *
 * Canon: `listto-app`, `docs/SYNC_PROGRESS_KIT.md` (2026-08-16).
 */

/**
 * Per-stream counters as reported by the core extension.
 *
 * Structurally compatible with `ProgressWithOperations` from
 * `@powersync/common`, so a `SyncStreamStatus` can be passed straight in.
 */
export interface StreamProgressCounters {
  totalOperations: number;
  downloadedOperations: number;
}

/**
 * The slice of `SyncStreamStatus` (`@powersync/common`) this module reads.
 *
 * Declared structurally rather than imported so the aggregation stays a pure,
 * dependency-free function that tests can drive with plain object literals.
 */
export interface StreamStatusLike {
  /**
   * Counters for the in-flight download, or `null` when nothing is being
   * downloaded. PowerSync clears this between sync iterations.
   */
  progress: StreamProgressCounters | null;
  subscription: {
    name: string;
    /** True once this stream has completed at least one full sync. */
    hasSynced?: boolean;
  };
}

/** Aggregated, platform-independent view of the first-sync download. */
export interface StreamAggregateProgress {
  /**
   * Download progress as a number in `[0, 1]`, or `null` when no stream is
   * reporting counters (i.e. nothing is downloading right now, or the sync
   * client has not produced a status line yet).
   *
   * `null` means "unknown", NOT "zero" — render an indeterminate spinner, not
   * a 0% bar.
   */
  downloadedFraction: number | null;
  /** Operations already downloaded across all streams, or `null` if unknown. */
  downloadedOperations: number | null;
  /** Operations to download across all streams, or `null` if unknown. */
  totalOperations: number | null;
  /**
   * True when EVERY tracked stream reports at least one completed sync.
   *
   * This is the Tauri-safe replacement for `SyncStatus.hasSynced`. It is
   * deliberately false when no streams are tracked yet — see
   * {@link streamsHaveSynced}.
   */
  hasSynced: boolean;
  /** How many streams the sync client is tracking. */
  streamCount: number;
  /** How many of those have completed at least one sync. */
  syncedStreamCount: number;
}

const UNKNOWN_PROGRESS: StreamAggregateProgress = {
  downloadedFraction: null,
  downloadedOperations: null,
  totalOperations: null,
  hasSynced: false,
  streamCount: 0,
  syncedStreamCount: 0,
};

/**
 * True when every tracked stream has completed at least one sync.
 *
 * An EMPTY stream list returns `false`, never `true`. This is the whole
 * correctness question of this module: `[].every(...)` is vacuously true, and
 * an empty list is exactly what the SDK reports in the window between opening
 * the database and receiving the first status line from the sync service.
 * Reading that as "everything synced" dismisses the first-sync UI instantly on
 * a cold start and drops the user into an empty app.
 */
export function streamsHaveSynced(
  streams: readonly StreamStatusLike[] | null | undefined,
): boolean {
  if (!streams || streams.length === 0) {
    return false;
  }
  return streams.every((stream) => stream.subscription.hasSynced === true);
}

/**
 * Aggregates per-stream counters into a single progress figure.
 *
 * Streams whose `progress` is `null` (not currently downloading) contribute
 * nothing to the totals but still count towards {@link streamsHaveSynced}.
 */
export function aggregateStreamProgress(
  streams: readonly StreamStatusLike[] | null | undefined,
): StreamAggregateProgress {
  if (!streams || streams.length === 0) {
    return UNKNOWN_PROGRESS;
  }

  let total = 0;
  let downloaded = 0;
  let syncedStreams = 0;
  let reportingStreams = 0;

  for (const stream of streams) {
    if (stream.subscription.hasSynced === true) {
      syncedStreams += 1;
    }
    const { progress } = stream;
    if (!progress) {
      continue;
    }
    reportingStreams += 1;
    total += progress.totalOperations;
    downloaded += progress.downloadedOperations;
  }

  const hasSynced = syncedStreams === streams.length;

  if (reportingStreams === 0) {
    return {
      ...UNKNOWN_PROGRESS,
      hasSynced,
      streamCount: streams.length,
      syncedStreamCount: syncedStreams,
    };
  }

  return {
    // `total === 0` happens for a fraction of a second before the checkpoint
    // lands: counters exist but there is nothing to download yet. Report 0, the
    // same value `SyncProgress.untilPriority` returns for an empty bucket set;
    // the UI decides whether 0-of-0 deserves an indeterminate spinner.
    downloadedFraction: total === 0 ? 0 : clampFraction(downloaded / total),
    downloadedOperations: downloaded,
    totalOperations: total,
    hasSynced,
    streamCount: streams.length,
    syncedStreamCount: syncedStreams,
  };
}

function clampFraction(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(1, Math.max(0, value));
}
